/**
 * P3-S2 릴레이 구간 관측(FIELD-DEC-003 §3.2 · §5 ④) — 룸 RelayCtl.step 과 학생 '내 구간 시험'(local-host)이 **같은 규칙**으로
 * 지금 구간 하나를 판정한다. 순수 함수 · 커널 의존 없음(월드 조회는 q 로 받는다 — 지난 스텝 뒤 커널 상태).
 *
 *  - 내보내는 것 = 레버: 기계가 레버 조각에 닿음 **또는** 구간 물체 중심이 나감 상자 안(물체가 있을 때) → exit
 *  - 그 밖(공 · 존): 구간 물체 중심이 나감 상자 안(경계 포함) → exit · 물체 y < lostBelow → lost
 *  - 기계 차체 중심 y < lostBelow → lost(어느 경우든)
 *  - 같은 틱에 exit 와 lost 가 함께 오면 RelayMachine 이 exit 를 먼저 본다(나감이 이긴다)
 * lostBelow 는 모드 정의 relay 블록(def.lostBelow) — 없으면 lost 판정을 하지 않는다.
 */
import type { RelayStagePlan } from "./relay";
import type { RelayDef, RelayTrigger, Slot, Vec3 } from "./types";

/** 구간 하나(relayLayout 결과의 월드 좌표 — {@link RelaySection} 이 이 모양을 채운다) */
export interface RelaySectionInfo {
  readonly index: number;
  readonly machineSlot: Slot;
  /** 들어오는 공 자리(in = ball) */
  readonly entry: Slot;
  readonly exitBox: { readonly center: Vec3; readonly half: Vec3 };
  /** 레버 조각 id(out = lever) */
  readonly lever?: string;
  /** 구간 물체 조각 id(공 · 블록) · 처음 자리 */
  readonly objectId?: string;
  readonly objectSlot?: Slot;
  readonly inKind: RelayTrigger;
  readonly outKind: RelayTrigger;
}

/** 관측에 필요한 월드 조회(m · 지난 스텝 뒤 커널 상태) — 룸은 기계 번호, 로컬은 그 번호를 기계 id 로 바꿔 부른다 */
export interface RelayStageQuery {
  /** 조각(동적 물체) 중심 — 모르면 null */
  objectAt(pieceId: string): readonly [number, number, number] | null;
  /** 그 기계가 조각(레버)에 닿았나(지난 스텝 접촉) */
  touches(machine: number, pieceId: string): boolean;
  /** 차체 중심 — 모르면 null */
  chassis(machine: number): readonly [number, number, number] | null;
}

/** 관측 결과 — RelayMachine.step 의 RelayObservation 에 그대로(present 는 부르는 쪽이 더한다) */
export interface RelayStageSeen {
  exit?: boolean;
  lost?: boolean;
}

/** 점이 상자 안(경계 포함)인가 */
export function inRelayBox(
  p: readonly number[] | null,
  b: { readonly center: Vec3; readonly half: Vec3 },
): boolean {
  return (
    !!p &&
    Math.abs(p[0]! - b.center[0]) <= b.half[0] &&
    Math.abs(p[1]! - b.center[1]) <= b.half[1] &&
    Math.abs(p[2]! - b.center[2]) <= b.half[2]
  );
}

/**
 * 지금 구간 관측 — Running 인 틱에만 부른다(Armed 창 · Failed 동안은 부르는 쪽이 건너뛴다 — 룸 RelayCtl 과 같다).
 * @param section 그 구간(relayLayout sections[i])
 * @param plan 그 구간 배치(기계 번호만 쓴다)
 * @param q 월드 조회
 * @param def 모드 정의 relay 블록(lostBelow) — 없으면 lost 없음
 */
export function observeRelayStage(
  section: RelaySectionInfo,
  plan: Pick<RelayStagePlan, "machine">,
  q: RelayStageQuery,
  def?: Pick<RelayDef, "lostBelow"> | null,
): RelayStageSeen {
  const out: RelayStageSeen = {};
  const lostBelow = def?.lostBelow ?? null;
  const m = plan.machine;
  if (section.outKind === "lever" && section.lever) {
    out.exit =
      q.touches(m, section.lever) ||
      (!!section.objectId && inRelayBox(q.objectAt(section.objectId), section.exitBox));
  } else if (section.objectId) {
    const p = q.objectAt(section.objectId);
    out.exit = inRelayBox(p, section.exitBox);
    if (lostBelow !== null && p && p[1] < lostBelow) out.lost = true;
  }
  if (lostBelow !== null) {
    const c = q.chassis(m);
    if (c && c[1] < lostBelow) out.lost = true;
  }
  return out;
}
